import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Bar, Line } from 'react-chartjs-2';
import Chart from 'chart.js';
import {
  barChartOptions,
  lineChartOptions,
  crosshairPlugin,
  clickedBarHighlightPlugin,
  calculateYAxisMax,
  calculateYAxisStepSize,
  CHART_LAYOUT,
  createYAxisConfig as createYAxisConfigHelper,
  createXAxisConfig as createXAxisConfigHelper,
} from '../utils/chartHelpers';
import {
  ChartColumnStacked,
  ChartLine,
  ChartNoAxesCombined,
  Fullscreen,
  Minimize,
} from 'lucide-react';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import { CHART_CONFIG, SPLIT_VIEW_OPTIONS } from '../utils/constants';
import FilterBadges from './FilterBadges';
import HoverTooltip from './HoverTooltip';

if (Chart && Chart.plugins && Chart.plugins.register) {
  Chart.plugins.register(crosshairPlugin);
  Chart.plugins.register(clickedBarHighlightPlugin);
}

const ChartSection = ({
  activeChart,
  setActiveChart,
  splitView,
  setSplitView,
  isFullscreen,
  setIsFullscreen,
  barChartData,
  lineChartData,
  setSelectedDayIndex,
  setClickedDayIndex,
  clickedBarInfo,
  setClickedBarInfo,
  activeFilter,
  defaultFilters,
  dateRangePicker,
  myAppointmentsRef,
  filters,
  setTempFilters,
  setFilters,
  originalDateLabels,
  setActiveFilter,
  isLoadingChartData = false,
  overviewData,
  chartLabels,
  hiddenDatasets,
  setHiddenDatasets,
  isMobileOrTablet,
  statusOptions = [],
  showQuickFilters = true,
  chartTitle = 'Appointment',
  showSplitViewSelector = true,
  splitViewOptions = SPLIT_VIEW_OPTIONS,
  showFilterBadges = true,
}) => {
  const crosshairLockedRef = useRef(false);
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const chartWrapperRef = useRef(null);
  const dragOccurredRef = useRef(false); // Track if drag occurred to prevent click locking

  const barLabelsLength = useMemo(
    () => barChartData?.labels?.length || 0,
    [barChartData?.labels?.length]
  );
  const lineLabelsLength = useMemo(
    () => lineChartData?.labels?.length || 0,
    [lineChartData?.labels?.length]
  );

  const lineDatasetsKey = useMemo(() => {
    const datasets = lineChartData?.datasets || [];
    return datasets
      .filter((ds) => !ds.hidden)
      .map((ds) => ds.label)
      .join(',');
  }, [lineChartData?.datasets]);

  const barDatasetsKey = useMemo(() => {
    const datasets = barChartData?.datasets || [];
    return datasets
      .filter((ds) => !ds.hidden)
      .map((ds) => ds.label)
      .join(',');
  }, [barChartData?.datasets]);

  const [needsScroll, setNeedsScroll] = useState(false);
  const [hoveredBarIndex, setHoveredBarIndex] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null); // { dataIndex, datasetIndex }
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [clickedBarIndex, setClickedBarIndex] = useState(null);
  const tooltipHistoryPushedRef = useRef(false);

  // Plugin to handle dynamic hover colors based on hovered segment
  // This plugin draws a darker overlay on the specifically hovered segment
  const segmentHoverPlugin = useMemo(
    () => ({
      id: 'segmentHover',
      afterDraw: function (chart) {
        const hoveredSegment = chart.options?.hoveredSegment;
        if (
          !hoveredSegment ||
          hoveredSegment.dataIndex === null ||
          hoveredSegment.datasetIndex === null
        ) {
          return;
        }

        const { dataIndex, datasetIndex } = hoveredSegment;
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;

        if (
          !chartArea ||
          !chart.data?.datasets ||
          datasetIndex >= chart.data.datasets.length
        ) {
          return;
        }

        try {
          // Get the meta for the hovered dataset
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || !meta.data || !meta.data[dataIndex]) {
            return;
          }

          const element = meta.data[dataIndex];
          if (!element || !element._model) {
            return;
          }

          const model = element._model;
          const barLeft = model.x - model.width / 2;
          const barRight = model.x + model.width / 2;
          const barTop = Math.min(model.y, model.base);
          const barBottom = Math.max(model.y, model.base);

          // Draw a darker overlay on this specific segment
          ctx.save();
          ctx.globalCompositeOperation = 'multiply';
          ctx.fillStyle = 'rgba(0, 0, 0, 0.15)'; // Darken effect
          ctx.fillRect(barLeft, barTop, barRight - barLeft, barBottom - barTop);
          ctx.restore();
        } catch (e) {
          console.error('Error drawing segment hover overlay:', e);
        }
      },
    }),
    []
  );

  useEffect(() => {
    const handlePopState = (event) => {
      // If tooltip is open and back button is pressed, close it
      if (
        isMobileOrTablet &&
        clickedBarIndex !== null &&
        tooltipHistoryPushedRef.current
      ) {
        setClickedBarIndex(null);
        setHoveredBarIndex(null);
        setTooltipPosition(null);
        tooltipHistoryPushedRef.current = false;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMobileOrTablet, clickedBarIndex]);

  // Register the segment hover plugin
  useEffect(() => {
    if (Chart && Chart.plugins && Chart.plugins.register) {
      const isRegistered = Chart.plugins
        .getAll()
        .find((p) => p.id === 'segmentHover');
      if (!isRegistered) {
        Chart.plugins.register(segmentHoverPlugin);
      }
    }
  }, [segmentHoverPlugin]);

  const calculateTooltipPosition = useCallback(
    (chart, dataIndex, evt = null) => {
      if (!chart.canvas || !chart.chartArea) return null;

      const canvas = chart.canvas;
      const rect = canvas.getBoundingClientRect();
      const scales = chart.scales || {};
      const xScaleKey = Object.keys(scales).find(
        (key) => key.includes('x') || scales[key].isHorizontal
      );
      const xScale = xScaleKey ? scales[xScaleKey] : null;

      if (!xScale) {
        if (evt) {
          const tooltipWidth = 500;
          const padding = 20;
          const viewportWidth = window.innerWidth;
          let tooltipX = evt.clientX + 20;
          if (tooltipX + tooltipWidth > viewportWidth - padding) {
            tooltipX = evt.clientX - tooltipWidth - 20;
          }
          return {
            x: Math.max(
              padding,
              Math.min(tooltipX, viewportWidth - tooltipWidth - padding)
            ),
            y: Math.max(padding, evt.clientY - 200),
          };
        }
        return null;
      }

      try {
        const barCenterX = xScale.getPixelForValue(dataIndex);
        const tooltipWidth = 500;
        const padding = 20;
        const offset = 20;
        let tooltipX = rect.left + barCenterX + offset;
        let tooltipY = rect.top + chart.chartArea.top - 10;

        const viewportWidth = window.innerWidth;
        const viewportRight = viewportWidth - padding;

        if (tooltipX + tooltipWidth > viewportRight) {
          const leftPosition = rect.left + barCenterX - tooltipWidth - offset;
          if (leftPosition >= padding) {
            tooltipX = leftPosition;
            if (isFullscreen) {
              tooltipX -= 60;
              if (dataIndex === barChartData.labels.length - 1) {
                tooltipX -= 20;
              }
            }
          } else {
            tooltipX = viewportRight - tooltipWidth;
          }
        }

        if (tooltipY < padding) {
          tooltipY = rect.top + chart.chartArea.bottom + 10;
        }

        if (tooltipX < padding) {
          tooltipX = padding;
        }

        if (tooltipX + tooltipWidth > viewportWidth) {
          tooltipX = viewportWidth - tooltipWidth - padding;
        }

        if (isFullscreen) {
          tooltipX += 30;
        }

        return { x: tooltipX, y: tooltipY };
      } catch (e) {
        if (evt) {
          const tooltipWidth = 500;
          const padding = 20;
          const viewportWidth = window.innerWidth;
          let tooltipX = evt.clientX + 20;
          if (tooltipX + tooltipWidth > viewportWidth - padding) {
            tooltipX = evt.clientX - tooltipWidth - 20;
          }
          return {
            x: Math.max(
              padding,
              Math.min(tooltipX, viewportWidth - tooltipWidth - padding)
            ),
            y: Math.max(padding, evt.clientY - 200),
          };
        }
        return null;
      }
    },
    [isFullscreen]
  );

  useEffect(() => {
    if (!scrollContainerRef.current) {
      setNeedsScroll(false);
      return;
    }

    const calculateNeedsScroll = () => {
      if (!scrollContainerRef.current) {
        setNeedsScroll(false);
        return;
      }

      const containerWidth = scrollContainerRef.current.clientWidth || 0;
      const labelsLength =
        activeChart === 'bar' ? barLabelsLength : lineLabelsLength;

      if (labelsLength > 0) {
        const requiredWidth = labelsLength * CHART_CONFIG.BAR_THICKNESS;
        setNeedsScroll(requiredWidth > containerWidth);
      } else {
        setNeedsScroll(false);
      }
    };

    const rafId = requestAnimationFrame(() => {
      calculateNeedsScroll();
    });

    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateNeedsScroll, 150);
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeChart, barLabelsLength, lineLabelsLength, isFullscreen]);

  useEffect(() => {
    if (!needsScroll || !scrollContainerRef.current) return;

    const scrollToRight = () => {
      if (scrollContainerRef.current) {
        const maxScroll =
          scrollContainerRef.current.scrollWidth -
          scrollContainerRef.current.clientWidth;
        scrollContainerRef.current.scrollLeft = maxScroll > 0 ? maxScroll : 0;
      }
    };

    const rafId = requestAnimationFrame(scrollToRight);
    return () => cancelAnimationFrame(rafId);
  }, [
    barLabelsLength,
    lineLabelsLength,
    activeChart,
    isFullscreen,
    needsScroll,
  ]);

  // Force Chart.js to resize when chart width changes
  useEffect(() => {
    if (!chartInstanceRef.current || !chartWrapperRef.current) return;

    const chart = chartInstanceRef.current.chart || chartInstanceRef.current;
    if (!chart?.resize) return;

    const rafId = requestAnimationFrame(() => {
      const wrapperWidth = chartWrapperRef.current?.offsetWidth;
      if (wrapperWidth && wrapperWidth > 0) {
        chart.resize();
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [barLabelsLength, lineLabelsLength, activeChart, isFullscreen]);

  // Drag-to-scroll functionality
  useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container) return;

    // Use needsScroll state to determine if scrolling is needed
    const shouldScroll = needsScroll;

    // Clean up any existing scrollbar hiding classes first
    const existingClasses = Array.from(container.classList);
    existingClasses.forEach((cls) => {
      if (cls.startsWith('chart-scroll-')) {
        container.classList.remove(cls);
      }
    });

    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let isDraggingScroll = false;
    let rafId = null;
    let currentMouseX = 0;
    let hasMovedThreshold = false;

    // Optimized scroll update using requestAnimationFrame
    const updateScroll = () => {
      if (!isDraggingScroll || !container) {
        rafId = null;
        return;
      }

      const deltaX = currentMouseX - dragStartX;

      // Only scroll if moved beyond threshold
      if (Math.abs(deltaX) > 3) {
        if (!hasMovedThreshold) {
          hasMovedThreshold = true;
          dragOccurredRef.current = true;
        }

        const walk = -deltaX * 1.5;
        const newScrollLeft = dragStartScrollLeft + walk;

        const maxScroll = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.max(0, Math.min(maxScroll, newScrollLeft));
      }

      rafId = requestAnimationFrame(updateScroll);
    };

    const handleMouseDown = (e) => {
      if (e.button !== 0) return;

      if (e.target.closest('button') || e.target.closest('[role="button"]')) {
        return;
      }

      if (e.target.tagName === 'CANVAS' || e.target === container) {
        isDraggingScroll = true;
        dragOccurredRef.current = false;
        hasMovedThreshold = false;
        dragStartX = e.clientX;
        currentMouseX = e.clientX;
        dragStartScrollLeft = container.scrollLeft;

        container.style.cursor = 'grabbing';
        container.style.userSelect = 'none';
        container.style.willChange = 'scroll-position';

        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(updateScroll);
      }
    };

    const handleMouseMove = (e) => {
      if (!isDraggingScroll) return;

      currentMouseX = e.clientX;

      if (hasMovedThreshold) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleMouseUp = (e) => {
      if (isDraggingScroll) {
        isDraggingScroll = false;

        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        container.style.cursor = shouldScroll ? 'grab' : 'default';
        container.style.userSelect = '';
        container.style.willChange = 'auto';

        setTimeout(() => {
          dragOccurredRef.current = false;
        }, 50);
      }
    };

    const handleMouseLeave = () => {
      if (isDraggingScroll) {
        isDraggingScroll = false;

        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        container.style.cursor = shouldScroll ? 'grab' : 'default';
        container.style.userSelect = '';
        container.style.willChange = 'auto';
      }
    };

    container.style.cursor = shouldScroll ? 'grab' : 'default';

    container.style.overflowX = shouldScroll ? 'scroll' : 'hidden';

    if (shouldScroll) {
      container.style.scrollbarWidth = 'none'; // Firefox
      container.style.msOverflowStyle = 'none'; // IE/Edge

      const styleId = 'chart-scrollbar-hide';
      let styleElement = document.getElementById(styleId);
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }

      const containerId = `chart-scroll-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      container.classList.add(containerId);

      styleElement.textContent = `
        .${containerId}::-webkit-scrollbar {
          display: none;
        }
      `;
    } else {
      const existingClasses = Array.from(container.classList);
      existingClasses.forEach((cls) => {
        if (cls.startsWith('chart-scroll-')) {
          container.classList.remove(cls);
        }
      });
    }

    container.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (container) {
        container.style.willChange = 'auto';
        container.style.cursor = '';
        container.style.userSelect = '';
      }

      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [
    needsScroll,
    activeChart,
    barLabelsLength,
    lineLabelsLength,
    isFullscreen,
  ]);

  const unlockCrosshair = useCallback(
    (chart) => {
      crosshairLockedRef.current = false;
      if (chart && chart._crosshair) {
        chart._crosshair.locked = false;
        chart._crosshair.visible = false;
        chart.draw();
      }
      if (setSelectedDayIndex) {
        setSelectedDayIndex(null);
      }
      setClickedBarInfo(null);
    },
    [setSelectedDayIndex, setClickedBarInfo]
  );

  const lockCrosshairOnDoubleClick = useCallback(
    (chart, evt, activeElements, dataIndex) => {
      const canvas = chart.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = evt.clientX - rect.left;
      const y = evt.clientY - rect.top;
      const chartArea = chart.chartArea;

      if (
        chartArea &&
        x >= chartArea.left &&
        x <= chartArea.right &&
        y >= chartArea.top &&
        y <= chartArea.bottom
      ) {
        const scales = chart.scales || {};
        const xScaleKey = Object.keys(scales).find(
          (key) => key.includes('x') || scales[key].isHorizontal
        );
        const xScale = xScaleKey ? scales[xScaleKey] : null;

        let barCenterX = x;
        if (xScale) {
          try {
            barCenterX = xScale.getPixelForValue(dataIndex);
          } catch (e) {
            barCenterX = x;
          }
        }

        // Lock crosshair at bar center position
        crosshairLockedRef.current = true;
        if (!chart._crosshair) {
          chart._crosshair = {};
        }
        chart._crosshair.x = barCenterX;
        chart._crosshair.y = y;
        chart._crosshair.visible = true;
        chart._crosshair.locked = true;
        chart.draw();

        let isSameBarClicked = false;
        if (chart.data && chart.data.datasets) {
          const datasetValues = chart.data.datasets.map((dataset) => ({
            label: dataset.label,
            value: dataset.data?.[dataIndex] || 0,
            backgroundColor: dataset.backgroundColor,
          }));

          let clickedSegment = null;

          if (activeElements && activeElements.length > 0) {
            let bestMatch = null;
            let minDistance = Infinity;

            for (let i = 0; i < activeElements.length; i++) {
              const elem = activeElements[i];

              if (elem._index !== dataIndex) {
                continue;
              }

              const datasetIndex = elem._datasetIndex;
              if (
                datasetIndex === undefined ||
                datasetIndex < 0 ||
                datasetIndex >= datasetValues.length ||
                datasetValues[datasetIndex].value <= 0
              ) {
                continue;
              }

              if (elem._model) {
                const segmentTop = elem._model.y;
                const segmentBottom = elem._model.base;

                // Determine min/max (accounting for inverted Y axis)
                const segmentMin = Math.min(segmentTop, segmentBottom);
                const segmentMax = Math.max(segmentTop, segmentBottom);

                // Check if clicked Y is within this segment's pixel bounds
                if (y >= segmentMin && y <= segmentMax) {
                  // Calculate distance to center for tie-breaking
                  const segmentCenter = (segmentMin + segmentMax) / 2;
                  const distance = Math.abs(y - segmentCenter);

                  if (!bestMatch || distance < minDistance) {
                    bestMatch = datasetIndex;
                    minDistance = distance;
                  }
                }
              }
            }

            if (bestMatch !== null) {
              clickedSegment = datasetValues[bestMatch];
            }
          }

          const dateLabel =
            originalDateLabels && originalDateLabels[dataIndex]
              ? originalDateLabels[dataIndex]
              : chart.data.labels?.[dataIndex] || '';

          isSameBarClicked =
            clickedBarInfo &&
            clickedBarInfo.dataIndex === dataIndex &&
            clickedBarInfo.clickedSegment &&
            clickedSegment &&
            clickedBarInfo.clickedSegment.label === clickedSegment.label;

          if (isSameBarClicked) {
            if (setClickedBarInfo) {
              setClickedBarInfo(null);
            }
            if (setClickedDayIndex) {
              setClickedDayIndex(null);
            }
            if (setSelectedDayIndex) {
              setSelectedDayIndex(null);
            }
          } else {
            const barInfo = {
              date: dateLabel,
              dataIndex,
              clickedSegment: clickedSegment
                ? {
                    label: clickedSegment.label,
                    value: clickedSegment.value,
                  }
                : null,
              allSegments: datasetValues,
              total: datasetValues.reduce((sum, ds) => sum + ds.value, 0),
            };

            if (setClickedBarInfo) {
              setClickedBarInfo(barInfo);
            }
          }
        }

        if (!isSameBarClicked) {
          if (setClickedDayIndex) {
            setClickedDayIndex(dataIndex);
          }
          // Also update hover selection for display
          if (setSelectedDayIndex) {
            setSelectedDayIndex(dataIndex);
          }
        }
      }
    },
    [
      setClickedBarInfo,
      setClickedDayIndex,
      setSelectedDayIndex,
      originalDateLabels,
      clickedBarInfo,
    ]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        crosshairLockedRef.current &&
        chartContainerRef.current &&
        !chartContainerRef.current.contains(event.target)
      ) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (
          (myAppointmentsRef?.current &&
            myAppointmentsRef.current.contains(event.target)) ||
          event.target.localName === 'svg' ||
          event.target.innerHtml?.includes('Modal')
        ) {
          return;
        }
        if (chartInstanceRef.current) {
          unlockCrosshair(chartInstanceRef.current);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [unlockCrosshair, myAppointmentsRef]);

  const lastClickRef = useRef({ time: 0, x: 0, y: 0, dataIndex: null });

  useEffect(() => {
    const handleClick = (e) => {
      if (!chartInstanceRef.current) return;

      const chart = chartInstanceRef.current.chart || chartInstanceRef.current;
      if (!chart || !chart.canvas) return;

      const canvas = chart.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const chartArea = chart.chartArea;

      if (
        !chartArea ||
        x < chartArea.left ||
        x > chartArea.right ||
        y < chartArea.top ||
        y > chartArea.bottom
      ) {
        return;
      }

      const activeElements = chart.getElementsAtEventForMode(
        e,
        'index',
        { intersect: true },
        false
      );

      if (!activeElements || activeElements.length === 0) {
        return;
      }

      const element = activeElements[0];
      const dataIndex = element._index;
      const now = Date.now();
      const lastClick = lastClickRef.current;

      const isDoubleClick =
        now - lastClick.time < 300 &&
        Math.abs(x - lastClick.x) < 10 &&
        Math.abs(y - lastClick.y) < 10 &&
        dataIndex === lastClick.dataIndex;

      if (isDoubleClick) {
        e.preventDefault();
        e.stopPropagation();

        lockCrosshairOnDoubleClick(chart, e, activeElements, dataIndex);

        lastClickRef.current = { time: 0, x: 0, y: 0, dataIndex: null };
      } else {
        lastClickRef.current = {
          time: now,
          x,
          y,
          dataIndex,
        };
      }
    };

    const attachListener = () => {
      if (chartInstanceRef.current) {
        const chart =
          chartInstanceRef.current.chart || chartInstanceRef.current;
        if (chart && chart.canvas) {
          chart.canvas.addEventListener('click', handleClick, {
            capture: true,
          });
          return () => {
            console.log('removing click listener', chart.canvas);
            if (chart && chart.canvas)
              chart.canvas.removeEventListener('click', handleClick, {
                capture: true,
              });
          };
        }
      }
      return null;
    };

    let cleanup = attachListener();

    const timer = setTimeout(() => {
      const newCleanup = attachListener();
      if (newCleanup) {
        if (cleanup) cleanup();
        cleanup = newCleanup;
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      if (cleanup) cleanup();
      if (chartInstanceRef.current) {
        const chart =
          chartInstanceRef.current.chart || chartInstanceRef.current;
        if (chart && chart.canvas) {
          chart.canvas.removeEventListener('click', handleClick, {
            capture: true,
          });
        }
      }
    };
  }, [lockCrosshairOnDoubleClick, activeChart, barChartData, lineChartData]);

  const parseISO = (s) => {
    if (!s) return null;

    // Month format: "2026-01" -> treat as first day of month
    if (/^\d{4}-\d{2}$/.test(s)) {
      return new Date(`${s}-01T00:00:00`);
    }

    // Day format: "2026-01-07"
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return new Date(`${s}T00:00:00`);
    }

    // Hour format with time: "2026-01-07 20-24" -> treat as hour:minute (20:24)
    if (/^\d{4}-\d{2}-\d{2}\s+\d{1,2}-\d{1,2}$/.test(s)) {
      // Split date and time
      const [datePart, timePart] = s.split(/\s+/);
      // Replace dash with colon to get hour:minute format
      const [hour, minute] = timePart.split('-');
      return new Date(
        `${datePart}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00`
      );
    }

    // Hour format with single hour: "2026-01-07 18"
    if (/^\d{4}-\d{2}-\d{2}\s+\d{1,2}$/.test(s)) {
      const [datePart, hour] = s.split(/\s+/);
      return new Date(`${datePart}T${hour.padStart(2, '0')}:00:00`);
    }

    // Try standard ISO format with colons: "2026-01-07 18:24"
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(s)) {
      return new Date(s.replace(' ', 'T'));
    }

    // Fallback: try to parse as-is
    return new Date(s);
  };

  const fmt = {
    day: new Intl.DateTimeFormat('en-US', { day: 'numeric' }),
    weekdayShort: new Intl.DateTimeFormat('en-US', { weekday: 'short' }),
    monthLong: new Intl.DateTimeFormat('en-US', { month: 'long' }),
    monthShort: new Intl.DateTimeFormat('en-US', { month: 'short' }),
    year: new Intl.DateTimeFormat('en-US', { year: 'numeric' }),
    timeHM: new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }),
  };

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const isSameMonth = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth();

  const isSameYear = (a, b) => a && b && a.getFullYear() === b.getFullYear();

  const makeTimeTickCallback = ({
    originalDateLabels,
    rangeType, // "HOUR" | "HOUR4" | "HOUR6" | "HOUR12" | "DAY" | "WEEK" | "MONTH"
    step = 4,
    isLoading = false,
  }) => {
    return (value, index) => {
      if (isLoading) {
        const shouldSample = index === 0 || index % step === 0;
        const totalLength = originalDateLabels?.length || 0;
        const isLastIndex = index === totalLength - 1;
        if (shouldSample || isLastIndex) {
          return '-';
        }
        return '';
      }

      const currRaw = originalDateLabels?.[index];
      const prevRaw = originalDateLabels?.[index - 1];
      const totalLength = originalDateLabels?.length || 0;
      const isLastIndex = index === totalLength - 1;

      const curr = parseISO(currRaw);
      const prev = parseISO(prevRaw);

      if (!curr) return '';

      const monthChanged = prev && !isSameMonth(curr, prev);
      const dayChanged = prev && !isSameDay(curr, prev);

      const yearChanged = prev && !isSameYear(curr, prev);

      const shouldSample = index === 0 || index % step === 0;

      if (rangeType === 'MONTH') {
        // Values are months; show year when crossing into previous year
        if (yearChanged) return fmt.year.format(curr); // "2026"
        if (shouldSample) return fmt.monthLong.format(curr); // "January"
        // Always show something for last index
        if (isLastIndex) return fmt.monthLong.format(curr);
        return '';
      }

      if (rangeType === 'DAY' || rangeType === 'WEEK') {
        if (monthChanged) {
          return fmt.monthLong.format(curr); // Show current month when entering it
        }

        if (shouldSample) return fmt.day.format(curr); // "6"

        if (isLastIndex) return fmt.day.format(curr);
        return '';
      }

      // Hours (HOUR/HOUR4/HOUR6/HOUR12):
      // show day number when day changes, otherwise show time at sample points
      if (dayChanged) return fmt.day.format(curr); // "4" (day of month)
      if (shouldSample) return fmt.timeHM.format(curr); // "12:34"

      if (isLastIndex) return fmt.timeHM.format(curr);
      return '';
    };
  };

  const rangeTypeForTick = dateRangePicker?.filterType;
  const tickCb = useMemo(
    () =>
      makeTimeTickCallback({
        originalDateLabels,
        rangeType: rangeTypeForTick,
        step: 4,
        isLoading: isLoadingChartData,
      }),
    [originalDateLabels, rangeTypeForTick, isLoadingChartData]
  );

  const createXAxisConfig = useCallback(
    (baseXAxis) => createXAxisConfigHelper(baseXAxis, tickCb),
    [tickCb]
  );

  const createOnHoverHandler = useCallback(
    (baseOnHover) =>
      function (evt, activeElements) {
        const chart = this.chart || this;
        if (chart && !chartInstanceRef.current) {
          chartInstanceRef.current = chart;
        }

        if (!crosshairLockedRef.current) {
          if (baseOnHover) {
            baseOnHover.call(this, evt, activeElements);
          }

          if (
            activeElements &&
            activeElements.length > 0 &&
            activeChart === 'bar'
          ) {
            const dataIndex = activeElements[0]._index;

            // Determine which specific segment is being hovered based on mouse Y position
            const canvas = chart.canvas;
            const rect = canvas.getBoundingClientRect();
            const y = evt.clientY - rect.top;
            const chartArea = chart.chartArea;

            let hoveredDatasetIndex = null;

            if (chartArea && y >= chartArea.top && y <= chartArea.bottom) {
              // Find which segment the mouse is over
              for (let i = 0; i < activeElements.length; i++) {
                const elem = activeElements[i];
                if (elem._index === dataIndex && elem._model) {
                  const segmentTop = elem._model.y;
                  const segmentBottom = elem._model.base;
                  const segmentMin = Math.min(segmentTop, segmentBottom);
                  const segmentMax = Math.max(segmentTop, segmentBottom);

                  // Check if mouse Y is within this segment's bounds
                  if (y >= segmentMin && y <= segmentMax) {
                    hoveredDatasetIndex = elem._datasetIndex;
                    break;
                  }
                }
              }
            }

            // Update hovered segment state
            setHoveredSegment(
              hoveredDatasetIndex !== null
                ? { dataIndex, datasetIndex: hoveredDatasetIndex }
                : { dataIndex, datasetIndex: null }
            );

            if (setSelectedDayIndex) {
              setSelectedDayIndex(dataIndex);
            }

            if (isFullscreen && activeChart === 'bar' && !isMobileOrTablet) {
              setHoveredBarIndex(dataIndex);
              const position = calculateTooltipPosition(chart, dataIndex, evt);
              if (position) {
                setTooltipPosition(position);
              }
            }
          } else {
            // No active elements - clear hover state
            setHoveredSegment(null);

            if (setSelectedDayIndex) {
              setSelectedDayIndex(null);
            }

            if (isFullscreen && activeChart === 'bar' && !isMobileOrTablet) {
              setHoveredBarIndex(null);
              setTooltipPosition(null);
            }
          }
        }
      },
    [
      setSelectedDayIndex,
      isFullscreen,
      activeChart,
      isMobileOrTablet,
      calculateTooltipPosition,
    ]
  );

  const createOnClickHandler = useCallback(
    () =>
      function (evt, activeElements) {
        const chart = this.chart || this;
        chartInstanceRef.current = chart;

        if (activeElements && activeElements.length > 0) {
          const element = activeElements[0];
          const dataIndex = element._index;
          const canvas = chart.canvas;
          const rect = canvas.getBoundingClientRect();
          const x = evt.clientX - rect.left;
          const y = evt.clientY - rect.top;
          const chartArea = chart.chartArea;

          if (isFullscreen && activeChart === 'bar' && isMobileOrTablet) {
            if (clickedBarIndex === dataIndex) {
              setClickedBarIndex(null);
              setHoveredBarIndex(null);
              setTooltipPosition(null);

              if (tooltipHistoryPushedRef.current) {
                window.history.back();
                tooltipHistoryPushedRef.current = false;
              }
            } else {
              setClickedBarIndex(dataIndex);
              setHoveredBarIndex(dataIndex);

              const position = calculateTooltipPosition(chart, dataIndex, evt);
              setTooltipPosition(position || { x: 0, y: 0 });

              if (isMobileOrTablet && !tooltipHistoryPushedRef.current) {
                window.history.pushState({ tooltipOpen: true }, '');
                tooltipHistoryPushedRef.current = true;
              }
            }

            dragOccurredRef.current = false;
            return;
          }

          if (dragOccurredRef.current) {
            return;
          }

          if (
            chartArea &&
            x >= chartArea.left &&
            x <= chartArea.right &&
            y >= chartArea.top &&
            y <= chartArea.bottom
          ) {
            lockCrosshairOnDoubleClick(chart, evt, activeElements, dataIndex);
          }
        } else {
          // Close tooltip when clicking outside (mobile/tablet)
          if (isFullscreen && activeChart === 'bar' && isMobileOrTablet) {
            setClickedBarIndex(null);
            setHoveredBarIndex(null);
            setTooltipPosition(null);

            if (tooltipHistoryPushedRef.current) {
              window.history.back();
              tooltipHistoryPushedRef.current = false;
            }
          }
          unlockCrosshair(chart);
          if (setClickedDayIndex) {
            setClickedDayIndex(null);
          }
          if (setClickedBarInfo) {
            setClickedBarInfo(null);
          }
        }
      },
    [
      lockCrosshairOnDoubleClick,
      unlockCrosshair,
      setClickedDayIndex,
      setClickedBarInfo,
      isFullscreen,
      activeChart,
      isMobileOrTablet,
      clickedBarIndex,
      calculateTooltipPosition,
    ]
  );

  const filterType = dateRangePicker?.filterType;

  const getBarChartOptionsWithHandlers = useCallback(() => {
    const yAxisMax = calculateYAxisMax(barChartData);
    const yAxisStepSize = calculateYAxisStepSize(yAxisMax);

    return {
      ...barChartOptions,
      clickedBarInfo,
      filterType,
      originalDateLabels,
      hoveredSegment, // Pass hovered segment to plugin
      layout: CHART_LAYOUT,
      scales: {
        ...barChartOptions.scales,
        xAxes: [createXAxisConfig(barChartOptions.scales.xAxes[0])],
        yAxes: [
          createYAxisConfigHelper(
            barChartOptions.scales.yAxes[0],
            yAxisMax,
            yAxisStepSize,
            true
          ),
        ],
      },
      onHover: createOnHoverHandler(barChartOptions.onHover),
      onClick: createOnClickHandler(),
    };
  }, [
    barChartData,
    clickedBarInfo,
    filterType,
    originalDateLabels,
    hoveredSegment,
    createXAxisConfig,
    createOnHoverHandler,
    createOnClickHandler,
  ]);

  const getLineChartOptionsWithHandlers = useCallback(() => {
    const yAxisMax = calculateYAxisMax(lineChartData, false);
    const yAxisStepSize = calculateYAxisStepSize(yAxisMax);

    return {
      ...lineChartOptions,
      originalDateLabels,
      layout: CHART_LAYOUT,
      scales: {
        ...lineChartOptions.scales,
        xAxes: [createXAxisConfig(lineChartOptions.scales.xAxes[0])],
        yAxes: [
          createYAxisConfigHelper(
            lineChartOptions.scales.yAxes[0],
            yAxisMax,
            yAxisStepSize,
            false
          ),
        ],
      },
      onHover: createOnHoverHandler(lineChartOptions.onHover),
      onClick: createOnClickHandler(),
      animation: false,
      transitions: {
        active: {
          animation: {
            duration: 0,
          },
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
      hover: {
        mode: 'index',
        intersect: false,
      },
    };
  }, [
    lineChartData,
    originalDateLabels,
    createXAxisConfig,
    createOnHoverHandler,
    createOnClickHandler,
  ]);

  const getChartWidth = useCallback(() => {
    if (activeChart === 'bar' && barLabelsLength > 0) {
      const calculatedWidth = barLabelsLength * CHART_CONFIG.BAR_THICKNESS;

      if (scrollContainerRef.current) {
        const containerWidth = scrollContainerRef.current.clientWidth;
        return calculatedWidth < containerWidth
          ? '100%'
          : `${calculatedWidth}px`;
      }
      return `${calculatedWidth}px`;
    }
    if (activeChart === 'line' && lineLabelsLength > 0) {
      const calculatedWidth = lineLabelsLength * CHART_CONFIG.BAR_THICKNESS;
      if (scrollContainerRef.current) {
        const containerWidth = scrollContainerRef.current.clientWidth;
        return calculatedWidth < containerWidth
          ? '100%'
          : `${calculatedWidth}px`;
      }
      return `${calculatedWidth}px`;
    }
    return '100%';
  }, [activeChart, barLabelsLength, lineLabelsLength]);

  const getYAxisOnlyOptions = useCallback(() => {
    const yAxisMax =
      activeChart === 'bar'
        ? calculateYAxisMax(barChartData)
        : calculateYAxisMax(lineChartData, false);
    const yAxisStepSize = calculateYAxisStepSize(yAxisMax);

    const baseOptions =
      activeChart === 'bar' ? barChartOptions : lineChartOptions;

    return {
      ...baseOptions,
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      events: [],
      tooltips: { enabled: false },
      hover: { mode: null },
      legend: { display: false },
      layout: {
        padding: {
          left: 0,
          right: -25,
          top: 35,
        },
      },
      scales: {
        xAxes: [
          {
            display: true,
            stacked: activeChart === 'bar',
            gridLines: { display: false },
            ticks: {
              ...baseOptions.scales.xAxes[0]?.ticks,
              color: 'transparent',
              callback: function () {
                return '';
              },
            },
          },
        ],
        yAxes: [
          {
            ...baseOptions.scales.yAxes[0],
            stacked: activeChart === 'bar',
            ticks: {
              fontFamily: 'Averta Bold',
              fontSize: 12,
              color: '#717784',
              padding: 8,
              max: yAxisMax,
              min: 0,
              stepSize: yAxisStepSize,
              display: true,
              callback: function (value, index, values) {
                // Filter out ticks that are too close to the max value
                // If a tick is less than half the stepSize away from max, don't show it
                // This prevents overlap when Chart.js adds an extra tick at max
                const threshold = yAxisStepSize * 0.5;
                if (value > yAxisMax - threshold && value < yAxisMax) {
                  return '';
                }
                return value;
              },
            },
            gridLines: { display: false },
          },
        ],
      },
    };
  }, [activeChart, barChartData, lineChartData]);

  const axisChartData = useCallback(() => {
    if (activeChart === 'bar') {
      return {
        ...barChartData,
        datasets: (barChartData?.datasets || []).map((ds) => ({
          ...ds,
          backgroundColor: 'rgba(0,0,0,0)',
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: 0,
          hoverBackgroundColor: 'rgba(0,0,0,0)',
          hoverBorderColor: 'rgba(0,0,0,0)',
        })),
      };
    }

    return {
      ...lineChartData,
      datasets: (lineChartData?.datasets || []).map((ds) => ({
        ...ds,
        borderColor: 'rgba(0,0,0,0)',
        backgroundColor: 'rgba(0,0,0,0)',
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 0,
        fill: false,
      })),
    };
  }, [activeChart, barChartData, lineChartData]);

  return (
    <div
      ref={chartContainerRef}
      key={`chart-${isFullscreen}`}
      className={`bg-white w-full box-border xl:w-2/3 border-solid border border-tertiary rounded-xl p-4 relative ${
        isFullscreen ? 'w-full' : 'flex-1'
      }`}
      style={isFullscreen ? { width: '100%' } : {}}>
      <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-0 md:items-center pb-3 border-b border-gray-2000 mb-4 border-solid border-l-0 border-r-0 border-t-0">
        <div className="flex items-center gap-2">
          <div className="text-lg font-averta-bold text-primary-text-secondary flex items-center gap-1">
            <ChartNoAxesCombined />
            {showQuickFilters && defaultFilters?.[activeFilter]
              ? `${defaultFilters[activeFilter]} `
              : ''}
            {chartTitle}{' '}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="hidden  border w-12 h-12 box-border border-solid border-light-bg-secondary text-gray-600 rounded cursor-pointer lg:flex items-center justify-center bg-white hover:bg-light-bg transition-colors"
            onClick={() => setIsFullscreen((prev) => !prev)}>
            {isFullscreen ? <Minimize /> : <Fullscreen />}
          </div>
          {showSplitViewSelector && splitViewOptions?.length > 0 && (
            <div>
              <CustomSelectEx
                options={splitViewOptions}
                value={
                  splitViewOptions.find(
                    (option) => option.value === splitView
                  ) || splitViewOptions[0]
                }
                onChange={(value) => setSplitView(value.value)}
                height={48}
                placeholder="Split View"
                width={150}
                dropdown={true}
                maxHeight={48}
              />
            </div>
          )}
          <div className="bg-light-bg rounded p-1 flex gap-2 h-12 box-border">
            <div
              className={`p-2 rounded cursor-pointer flex items-center gap-1 text-sm font-averta transition-all ${
                activeChart === 'bar'
                  ? 'bg-white text-gray-600 font-averta-bold font-bold'
                  : 'text-border-secondary hover:bg-white hover:bg-opacity-50'
              }`}
              onClick={() => setActiveChart('bar')}>
              <ChartColumnStacked />
              <span>Bar Chart</span>
            </div>
            <div
              className={`p-2 rounded cursor-pointer flex items-center gap-1 text-sm font-averta transition-all ${
                activeChart === 'line'
                  ? 'bg-white text-gray-600 font-averta-bold font-bold'
                  : 'text-border-secondary hover:bg-white hover:bg-opacity-50'
              }`}
              onClick={() => setActiveChart('line')}>
              <ChartLine />
              <span>Line Chart</span>
            </div>
          </div>
        </div>
      </div>

      {showFilterBadges && (
        <FilterBadges
          filters={filters}
          setFilters={setFilters}
          setTempFilters={setTempFilters}
          setActiveFilter={setActiveFilter}
          statusOptions={statusOptions}
          activeFilter={activeFilter}
        />
      )}

      <div
        className="relative flex"
        onMouseLeave={() => {
          if (isFullscreen && activeChart === 'bar') {
            setHoveredBarIndex(null);
            setHoveredSegment(null);
            setTooltipPosition(null);
          }
        }}>
        <div className="h-333 relative pt-2" style={{ marginRight: '-20px' }}>
          <div
            ref={chartWrapperRef}
            className="h-full"
            style={{
              flexShrink: 0,
              width: 70,
              flex: '0 0 70px',
            }}>
            <Bar
              key={`bar-axis-${barLabelsLength}-${barDatasetsKey}-${isFullscreen}`}
              data={axisChartData}
              options={getYAxisOnlyOptions()}
            />
          </div>
        </div>
        <div
          id="chart-wrapper"
          ref={scrollContainerRef}
          className="h-333 relative pt-2 w-full"
          style={{
            marginLeft: '-20px',
          }}>
          <div
            ref={chartWrapperRef}
            className="h-full"
            style={{
              width: getChartWidth(),
              minWidth: getChartWidth(),
              flexShrink: 0,
            }}>
            {activeChart === 'bar' ? (
              <Bar
                key={`bar-${barLabelsLength}-${barDatasetsKey}-${isFullscreen}`}
                ref={chartInstanceRef}
                data={barChartData}
                options={getBarChartOptionsWithHandlers()}
              />
            ) : (
              <Line
                key={`line-${lineLabelsLength}-${lineDatasetsKey}-${isFullscreen}`}
                ref={chartInstanceRef}
                data={lineChartData}
                options={getLineChartOptionsWithHandlers()}
              />
            )}
          </div>
        </div>
      </div>

      {isFullscreen && activeChart === 'bar' && overviewData && (
        <HoverTooltip
          overviewData={overviewData}
          splitView={splitView}
          defaultFilters={defaultFilters}
          hoveredBarIndex={isMobileOrTablet ? clickedBarIndex : hoveredBarIndex}
          chartLabels={chartLabels}
          originalDateLabels={originalDateLabels}
          position={tooltipPosition}
          hiddenDatasets={hiddenDatasets}
          setHiddenDatasets={setHiddenDatasets}
          onClose={() => {
            setClickedBarIndex(null);
            setHoveredBarIndex(null);
            setTooltipPosition(null);

            if (tooltipHistoryPushedRef.current) {
              window.history.back();
              tooltipHistoryPushedRef.current = false;
            }
          }}
          isMobileOrTablet={isMobileOrTablet}
          setClickedBarInfo={setClickedBarInfo}
        />
      )}
    </div>
  );
};

export default ChartSection;
